import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Share2 } from "lucide-react";
import { api, fileUrl, showSlug } from "@/lib/api";
import { toast } from "sonner";
import BookingFlow from "@/components/BookingFlow";
import StickyBookButton from "@/components/StickyBookButton";

export default function BookPage() {
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [params] = useSearchParams();
  const { slug } = useParams();
  const navigate = useNavigate();
  const showId = params.get("show");

  const scrollToForm = () => {
    document.querySelector('[data-testid="booking-flow"]')?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        if (slug) {
          // Clean URL /book/<show-slug> — resolve slug (or a raw id) to a show
          const { data: shows } = await api.get("/shows");
          const match = shows.find((s) => showSlug(s) === slug || s.id === slug);
          if (match) {
            const { data } = await api.get(`/shows/${match.id}`);
            if (!cancelled) setEvent(data);
          }
        } else if (showId) {
          // Legacy ?show=<id> links still work — upgrade the URL to the clean slug
          const { data } = await api.get(`/shows/${showId}`);
          if (!cancelled) {
            setEvent(data);
            navigate(`/book/${showSlug(data)}`, { replace: true });
          }
        } else {
          const { data } = await api.get("/event");
          if (!cancelled) setEvent(data);
        }
      } catch {}
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [slug, showId, navigate]);

  // Per-show page title + Open Graph / Twitter meta (helps JS-aware contexts;
  // guaranteed crawler previews are served by /api/share/book/<slug>)
  useEffect(() => {
    if (!event) return;
    const prevTitle = document.title;
    document.title = `${event.name} — The Band Sumiran`;
    const pageUrl = `${window.location.origin}/book/${showSlug(event)}`;
    const desc = ([event.date, event.time, event.venue].filter(Boolean).join(" · ") +
      (event.description ? ` — ${event.description}` : "")).slice(0, 280);
    const img = event.image_url ? fileUrl(event.image_url) : "";
    const tags = [
      ["property", "og:title", event.name],
      ["property", "og:description", desc],
      ["property", "og:url", pageUrl],
      ["name", "twitter:title", event.name],
      ["name", "twitter:description", desc],
      ...(img ? [["property", "og:image", img], ["name", "twitter:image", img]] : []),
    ];
    const touched = [];
    tags.forEach(([attr, key, content]) => {
      let el = document.head.querySelector(`meta[${attr}="${key}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
        touched.push({ el, prev: null });
      } else {
        touched.push({ el, prev: el.getAttribute("content") });
      }
      el.setAttribute("content", content);
    });
    return () => {
      document.title = prevTitle;
      touched.forEach(({ el, prev }) => {
        if (prev === null) el.remove();
        else el.setAttribute("content", prev);
      });
    };
  }, [event]);

  const share = async () => {
    // The /api/share link is server-rendered with this show's OG tags, so it
    // produces a rich preview card on WhatsApp/social; humans get redirected
    // back to this booking page.
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/share/book/${showSlug(event)}`;
    const text = `${event.name} — ${[event.date, event.venue].filter(Boolean).join(" · ")}`;
    if (navigator.share) {
      try { await navigator.share({ title: event.name, text, url }); return; }
      catch (e) { if (e?.name === "AbortError") return; }
    }
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Share link copied — paste it in WhatsApp to show the rich preview");
    } catch {
      toast.error("Could not copy link");
    }
  };

  return (
    <div className="min-h-screen bg-ink text-bone" data-testid="book-page">
      <div className="noise-overlay" aria-hidden="true" />
      <header className="px-6 md:px-12 h-[72px] flex items-center justify-between border-b border-bone/10 sticky top-0 bg-ink/90 backdrop-blur-xl z-40">
        <Link to="/" className="flex items-baseline gap-2" data-testid="book-logo">
          <span className="font-hindi text-2xl text-saffron">सुमिरन</span>
          <span className="font-display text-sm tracking-[0.25em] text-bone">THE BAND SUMIRAN</span>
        </Link>
        <div className="flex items-center gap-6">
          <Link to="/private" className="text-xs text-ash hover:text-saffron transition-colors" data-testid="book-private-link">Book a Private Show</Link>
          <Link to="/" className="text-xs text-ash hover:text-saffron transition-colors" data-testid="book-back-home">Back to site</Link>
        </div>
      </header>
      <main className="px-6 md:px-12 py-14 md:py-20 max-w-6xl mx-auto">
        {loading && <p className="text-ash" data-testid="book-loading">Loading…</p>}
        {!loading && !event && <p className="text-ash" data-testid="book-empty">No event on sale right now — check back soon.</p>}
        {event && (
          <>
            <p className="font-display text-xs tracking-[0.3em] text-saffron uppercase">Now Booking</p>
            <div className="mt-3 mb-12 flex flex-wrap items-start justify-between gap-4">
              <div className="max-w-3xl min-w-0">
                <h1 className="font-serif text-4xl sm:text-5xl text-bone leading-tight" data-testid="book-event-name">{event.name}</h1>
                {event.description && (
                  <p className="mt-4 text-sm md:text-base text-ash leading-relaxed" data-testid="book-event-description">{event.description}</p>
                )}
              </div>
              <button type="button" onClick={share}
                className="flex items-center gap-2 px-4 py-2.5 border border-bone/20 text-bone/80 text-xs tracking-wide hover:border-saffron hover:text-saffron transition-colors shrink-0"
                data-testid="book-share-btn">
                <Share2 size={14} /> Share
              </button>
            </div>
            <BookingFlow event={event} />
          </>
        )}
      </main>
      {event && <StickyBookButton onClick={scrollToForm} label="Book Now — Best Price" />}
    </div>
  );
}
